import type { RouteObjectEnhanced } from './type';
import { ENABLE_BUILDER_ANONYMOUS_MODE } from '@wegic/common';
import { LogoLoading, PageLoading } from '@wegic/ui'
import '@wegic/ui/dist/components/LogoLoading/index.css';
import '@wegic/ui/dist/components/PageLoading/index.css';;
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import RootLayout from '@/layouts/RootLayout';
import WorkspaceLayout from '@/layouts/WorkspaceLayout';
import RouteManager from '@/modules/RouteManager';

const loadProjectsPage = () => import('@/pages/Projects');

export function preloadProjectsPage() {
  return loadProjectsPage().then(() => undefined);
}

function lazyLoad(importFn: Parameters<typeof lazy>[0]) {
  const Component = lazy(importFn);
  return (
    <Suspense fallback={<PageLoading />}>
      <Component />
    </Suspense>
  );
}

function workspaceLazyLoad(importFn: Parameters<typeof lazy>[0]) {
  const Component = lazy(importFn);
  return (
    <Suspense fallback={<LogoLoading />}>
      <Component />
    </Suspense>
  );
}

// 重定向老项目路径 /app/* 到 /v1/app/*
function RedirectToV1App() {
  useEffect(() => {
    const newPath = window.location.href.replace('/app/', '/v1/app/');
    window.location.replace(newPath);
  }, []);

  return <PageLoading />;
}

function NavigateToWorkspaceProject() {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  const target = search
    ? `/workspace/projects/${projectId}?${search}`
    : `/workspace/projects/${projectId}`;

  return <Navigate to={target} replace />;
}

export const customRoutes: RouteObjectEnhanced[] = [
  {
    element: <RootLayout />,
    children: [
      {
        path: '/',
        element: <Navigate to="/workspace" replace />,
      },
      {
        element: <WorkspaceLayout />,
        children: [
          {
            path: '/workspace',
            element: workspaceLazyLoad(() => import('@/pages/Workspace')),
            meta: {
              requireLogin: true,
            },
          },
          {
            path: '/workspace/projects/:projectId?',
            element: <RouteManager>{workspaceLazyLoad(loadProjectsPage)}</RouteManager>,
            meta: {
              requireLogin: !ENABLE_BUILDER_ANONYMOUS_MODE,
            },
          },
          {
            path: '/workspace/trash',
            element: workspaceLazyLoad(() => import('@/pages/Trash')),
            meta: {
              requireLogin: true,
            },
          },
        ],
      },
      {
        path: '/login',
        element: lazyLoad(() => import('@/pages/Login')),
      },
      {
        path: '/verify-email-change',
        element: lazyLoad(() => import('@/pages/VerifyEmailChange')),
      },
      {
        path: '/projects',
        element: lazyLoad(() => import('@/pages/ProjectsRedirect')),
        meta: {
          requireLogin: !ENABLE_BUILDER_ANONYMOUS_MODE,
        },
      },
      {
        path: '/projects/:projectId',
        element: <NavigateToWorkspaceProject />,
      },
      {
        path: '/domain-connect/callback',
        element: lazyLoad(() => import('@/pages/DomainConnectCallback')),
      },
      {
        path: '/app/*',
        element: <RedirectToV1App />,
      },
      {
        path: '/feedback',
        element: lazyLoad(() => import('@/pages/Feedback')),
      },
      {
        path: '/maintenance',
        element: lazyLoad(() => import('@/pages/Maintenance')),
      },
      {
        path: '*',
        element: lazyLoad(() => import('@/pages/NotFound')),
      },
    ],
  },
];

window.customRoutes = customRoutes;
